// API base resolves in this order:
//   1) window.__API_BASE__  (set at runtime in public/config.js — editable on
//      the server, e.g. the HostGator demo, without rebuilding)
//   2) REACT_APP_API_URL    (build-time env)
//   3) the production default
const BASE_URL =
  (typeof window !== "undefined" && window.__API_BASE__) ||
  process.env.REACT_APP_API_URL ||
  "https://assistify-system-kuw2.vercel.app/api/v1";


function getToken() {
  return localStorage.getItem("access_token");
}

async function request(path, options = {}) {
  const token = getToken();
  const res = await fetch(`${BASE_URL}${path}`, {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
    ...options,
  });

  if (res.status === 401) {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    if (token) {
      return request(path, options);
    }
  }

  const data = await res.json();
  if (!res.ok) throw data;
  return data;
}


export async function register({ username, email, password, password2, role }) {
  return request("/auth/register/", {
    method: "POST",
    body: JSON.stringify({ username, email, password, password2, role }),
  });
}

export async function login({ email, password }) {
  const data = await request("/auth/login/", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
  localStorage.setItem("access_token", data.access);
  localStorage.setItem("refresh_token", data.refresh);
  return data; 
}

export function logout() {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
}

export async function getMe() {
  return request("/auth/me/");
}

export async function updateMe(data) {
  return request("/auth/me/", {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}


export async function fetchProducts(search = "") {
  // window.__PRODUCTS_JSON__ (set in config.js) serves the catalogue from a
  // local file on the same server — no external API.
  const local = typeof window !== "undefined" && window.__PRODUCTS_JSON__;
  if (local) {
    const items = await (await fetch(local)).json();
    const q = (search || "").trim().toLowerCase();
    if (!q) return items;
    const tokens = q.split(/\s+/).filter((t) => t.length >= 2);
    return items.filter((p) => {
      const hay = `${p.name} ${p.description} ${p.color} ${p.category_display}`.toLowerCase();
      return tokens.every((t) => hay.includes(t));
    });
  }
  const q = search ? `?search=${encodeURIComponent(search)}` : "";
  return request(`/products/${q}`);
}

export async function fetchProductById(id) {
  return request(`/products/${id}/`);
}


export async function fetchOffers() {
  if (typeof window !== "undefined" && window.__PRODUCTS_JSON__) return [];
  return request("/products/offers/");
}


export async function placeOrder({ customerEmail, paymentMethod, deliveryAddress, phone, items }) {
  return request("/orders/", {
    method: "POST",
    body: JSON.stringify({
      customer_email: customerEmail,
      payment_method: paymentMethod,
      delivery_address: deliveryAddress,
      phone,
      items,
    }),
  });
}

export async function fetchMyOrders() {
  return request("/orders/list/");
}

export async function fetchOrderByNumber(orderNumber) {
  return request(`/orders/${orderNumber}/`);
}

export async function submitReview({ orderId, rating, comment }) {
  return request("/orders/reviews/create/", {
    method: "POST",
    body: JSON.stringify({ order: orderId, rating, comment }),
  });
}


export async function sendChatMessage(message, conversationId = null) {
  // window.__CHAT_API__ (set in config.js) lets the chat use a self-hosted
  // endpoint (e.g. ./chat.php on the same server) instead of the default API.
  const chatUrl =
    (typeof window !== "undefined" && window.__CHAT_API__) || `${BASE_URL}/chat/`;
  const res = await fetch(chatUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ message, conversation_id: conversationId }),
  });
  const data = await res.json();
  return { reply: data.reply, conversationId: data.conversation_id, products: data.products || [] };
}

export async function fetchNotifications() {
  return request("/auth/notifications/");
}

export async function markNotificationAsRead(id) {
  return request(`/auth/notifications/${id}/mark-read/`, {
    method: "POST",
  });
}

export async function markAllNotificationsAsRead() {
  return request("/auth/notifications/mark-read/", {
    method: "POST",
  });
}
